/* Milk Maybe? — colors from the icon: carton blue, butter gold, cream. */
:root {
  --blue: #0d4f97;
  --blue-strong: #093a70;
  --gold: #c99526;
  --gold-soft: #f3e2b5;
  --bg: #fbf7ec;
  --surface: #ffffff;
  --surface-2: #f4efe1;
  --text: #1c2433;
  --muted: #5b6475;
  --line: #e3dccb;
  --accent: var(--blue);
  --on-accent: #ffffff;
  --danger: #b3261e;
  --ok: #2e7d32;
  --focus: #c99526;
  --radius: 14px;
  --tap: 44px;
  --shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 4px 16px rgb(13 79 151 / 8%);
  color-scheme: light;
  font-family: ui-rounded, 'SF Pro Rounded', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 17px;
  line-height: 1.4;
  color: var(--text);
  background: var(--bg);
  -webkit-tap-highlight-color: transparent;
}
@media (prefers-color-scheme: dark) {
  :root {
    --bg: #0e1726;
    --surface: #152237;
    --surface-2: #1b2b44;
    --text: #eef1f6;
    --muted: #a7b0c0;
    --line: #2a3a55;
    --accent: #e0b247;
    --on-accent: #10192a;
    --gold-soft: #3b3220;
    --danger: #ff8a80;
    --ok: #81c784;
    --focus: #e0b247;
    --shadow: 0 1px 2px rgb(0 0 0 / 40%);
    color-scheme: dark;
  }
}

* { box-sizing: border-box; }
html, body { margin: 0; min-height: 100%; background: var(--bg); }
body { overscroll-behavior-y: none; }
button, input, select, textarea { font: inherit; color: inherit; }
a { color: var(--accent); }
h1, h2, h3 { line-height: 1.2; margin: 0; }
:focus-visible { outline: 3px solid var(--focus); outline-offset: 2px; border-radius: 6px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.muted { color: var(--muted); }
.small { font-size: 0.85rem; }
.error { color: var(--danger); margin: 0; }
.stack { display: flex; flex-direction: column; gap: 12px; }
.row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.grow { flex: 1; min-width: 0; }
.center { display: grid; place-items: center; }
.full { min-height: 100dvh; }
.skip-link { position: absolute; left: 8px; top: -60px; z-index: 50; background: var(--surface); padding: 10px 14px; border-radius: 10px; }
.skip-link:focus { top: 8px; }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }

/* ---- controls */
input[type='text'], input[type='email'], input[type='password'], input[type='search'], input[type='number'], input:not([type]), select, textarea {
  min-height: var(--tap); padding: 8px 12px; border: 1.5px solid var(--line); border-radius: 12px; background: var(--surface); width: 100%;
}
input:focus, select:focus, textarea:focus { border-color: var(--accent); outline: none; box-shadow: 0 0 0 3px color-mix(in srgb, var(--focus) 45%, transparent); }
label.field { display: flex; flex-direction: column; gap: 4px; font-weight: 600; font-size: 0.9rem; }
label.field > span.hint { font-weight: 400; color: var(--muted); }
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: var(--tap); padding: 8px 16px;
  border-radius: 999px; border: 1.5px solid var(--line); background: var(--surface); cursor: pointer; text-decoration: none; color: var(--text);
  font-weight: 600; white-space: nowrap;
}
.btn:hover { border-color: var(--accent); }
.btn.primary { background: var(--accent); border-color: var(--accent); color: var(--on-accent); }
.btn.danger { color: var(--danger); }
.btn.ghost { border-color: transparent; background: transparent; }
.btn.big { width: 100%; font-size: 1.05rem; }
.btn:disabled { opacity: 0.55; cursor: default; }
.icon-btn {
  display: inline-grid; place-items: center; width: var(--tap); height: var(--tap); border-radius: 50%; border: 0; background: transparent;
  color: var(--muted); cursor: pointer; flex: none; text-decoration: none;
}
.icon-btn:hover { background: var(--surface-2); color: var(--text); }
.icon-btn.on { color: var(--accent); }
.link { background: none; border: 0; padding: 0; color: var(--accent); text-decoration: underline; cursor: pointer; }
.switch { display: flex; align-items: center; gap: 10px; min-height: var(--tap); cursor: pointer; }
.switch input { width: 22px; height: 22px; accent-color: var(--accent); }

/* ---- shell */
.shell { max-width: 760px; margin: 0 auto; padding: 0 12px calc(40px + env(safe-area-inset-bottom)); }
.topbar {
  position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 8px; padding: calc(8px + env(safe-area-inset-top)) 4px 8px;
  background: color-mix(in srgb, var(--bg) 92%, transparent); backdrop-filter: blur(10px);
}
.brand { display: flex; align-items: center; gap: 8px; font-weight: 800; font-size: 1.15rem; text-decoration: none; color: var(--text); }
.brand .q { color: var(--gold); }
.logo { border-radius: 22%; display: block; }
.topbar nav { margin-left: auto; display: flex; align-items: center; gap: 4px; }
.sync-pill { display: inline-flex; gap: 6px; align-items: center; padding: 4px 10px; border-radius: 999px; background: var(--surface-2); color: var(--muted); font-size: 0.8rem; text-decoration: none; }
.sync-pill.offline { background: var(--gold-soft); color: var(--text); }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
main { display: block; }
.page-head { display: flex; align-items: center; gap: 8px; margin: 8px 0 16px; }
.page-head h1 { font-size: 1.5rem; }

/* ---- store chips */
.stores-bar { display: flex; gap: 4px; padding: 4px 0 10px; align-items: center; }
.stores-bar [role='radiogroup'] { flex: 1; min-width: 0; overflow-x: auto; scrollbar-width: none; padding: 3px 2px; }
.stores-bar [role='radiogroup']::-webkit-scrollbar { display: none; }
.chip {
  display: inline-flex; align-items: center; gap: 6px; min-height: 38px; padding: 6px 14px; border-radius: 999px; border: 1.5px solid var(--line);
  background: var(--surface); cursor: pointer; white-space: nowrap; font-weight: 600; color: var(--text);
}
.chip[aria-checked='true'] { background: var(--accent); border-color: var(--accent); color: var(--on-accent); }
.chip .count { font-weight: 500; opacity: 0.8; font-size: 0.85em; }
.here { font-size: 0.85rem; color: var(--muted); margin: -4px 0 8px 4px; display: flex; align-items: center; gap: 4px; }

/* ---- add bar */
.add { position: sticky; top: calc(60px + env(safe-area-inset-top)); z-index: 9; background: var(--bg); padding: 4px 0 8px; }
.add form { display: flex; gap: 8px; }
.add input { font-size: 1.05rem; border-radius: 999px; padding-left: 18px; box-shadow: var(--shadow); }
.add .btn.primary { padding: 8px 18px; }
.add-hints { display: flex; flex-wrap: wrap; gap: 6px 12px; align-items: center; margin: 8px 6px 0; font-size: 0.9rem; color: var(--muted); }
.goes-to { display: inline-flex; align-items: center; gap: 4px; border: 1px dashed var(--line); border-radius: 999px; padding: 2px 10px; background: transparent; cursor: pointer; color: var(--text); min-height: 32px; }
.did-you-mean { color: var(--text); }
.suggestions { list-style: none; margin: 8px 0 0; padding: 4px; border-radius: var(--radius); background: var(--surface); box-shadow: var(--shadow); border: 1px solid var(--line); }
.suggestions li { display: flex; align-items: center; }
.suggestions .pick { flex: 1; text-align: left; border: 0; background: transparent; min-height: var(--tap); padding: 6px 12px; border-radius: 10px; cursor: pointer; display: flex; gap: 8px; align-items: baseline; }
.suggestions .pick:hover, .suggestions .pick:focus-visible { background: var(--surface-2); }
.suggestions .pick .where { color: var(--muted); font-size: 0.85rem; }
.suggestions .forget { font-size: 0.8rem; }

/* ---- the list */
.section { margin: 18px 0 0; }
.section > h2 { display: flex; align-items: center; gap: 8px; font-size: 0.95rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); margin: 0 6px 6px; }
.section > h2 .n { font-weight: 500; letter-spacing: 0; text-transform: none; }
.items { list-style: none; margin: 0; padding: 0; background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow); overflow: hidden; }
.item { display: flex; align-items: center; gap: 4px; padding: 2px 4px 2px 6px; min-height: 52px; border-top: 1px solid var(--line); }
.item:first-child { border-top: 0; }
.item .check { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; min-height: 48px; padding: 4px 6px; cursor: pointer; }
.item input[type='checkbox'] { appearance: none; flex: none; width: 26px; height: 26px; border: 2px solid var(--muted); border-radius: 50%; display: grid; place-items: center; cursor: pointer; margin: 0; background: var(--surface); }
.item input[type='checkbox']:checked { background: var(--accent); border-color: var(--accent); }
.item input[type='checkbox']:checked::after { content: ''; width: 7px; height: 13px; border: solid var(--on-accent); border-width: 0 3px 3px 0; transform: translateY(-2px) rotate(45deg); }
.item .text { display: flex; flex-direction: column; min-width: 0; }
.item .name { font-weight: 600; overflow-wrap: anywhere; }
.item .meta { font-size: 0.85rem; color: var(--muted); display: flex; gap: 6px; flex-wrap: wrap; }
.item .qty { color: var(--text); font-weight: 600; background: var(--gold-soft); border-radius: 6px; padding: 0 6px; }
.item .store-tag { border: 1px solid var(--line); border-radius: 6px; padding: 0 6px; }
.item.done .name { text-decoration: line-through; color: var(--muted); font-weight: 500; }
.item.flash { animation: flash 1.2s ease-out; }
@keyframes flash { from { background: var(--gold-soft); } to { background: transparent; } }
.cart-head { display: flex; align-items: center; justify-content: space-between; margin: 26px 6px 6px; }
.cart-head > h2 { font-size: 0.95rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); }
details.elsewhere { margin-top: 18px; }
details.elsewhere summary { cursor: pointer; color: var(--muted); padding: 10px 6px; min-height: var(--tap); }
.empty { text-align: center; padding: 48px 16px; color: var(--muted); }
.empty h2 { color: var(--text); margin-bottom: 8px; }
.empty img { margin: 0 auto 16px; }

/* ---- dialogs */
dialog.sheet {
  border: 0; padding: 0; border-radius: 20px; background: var(--surface); color: var(--text); width: min(520px, calc(100vw - 24px));
  max-height: calc(100dvh - 32px); box-shadow: 0 20px 60px rgb(0 0 0 / 30%);
}
dialog.sheet::backdrop { background: rgb(10 20 40 / 45%); }
@media (max-width: 600px) {
  dialog.sheet { margin: auto 0 0; width: 100vw; max-width: 100vw; border-radius: 20px 20px 0 0; padding-bottom: env(safe-area-inset-bottom); }
}
.sheet-head { display: flex; align-items: center; gap: 8px; padding: 14px 8px 6px 18px; }
.sheet-head h2 { font-size: 1.15rem; flex: 1; }
.sheet-body { padding: 4px 18px 18px; overflow: auto; }
.sheet-actions { display: flex; gap: 8px; justify-content: space-between; padding-top: 8px; }
.choices { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 6px; }
.choice { display: flex; align-items: center; gap: 10px; min-height: var(--tap); padding: 6px 12px; border-radius: 12px; border: 1.5px solid var(--line); cursor: pointer; background: var(--surface); width: 100%; text-align: left; }
.choice:hover { border-color: var(--accent); }
.choice[aria-pressed='true'], .choice:has(input:checked) { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 10%, var(--surface)); font-weight: 600; }
.choice input { width: 20px; height: 20px; accent-color: var(--accent); margin: 0; }
fieldset { border: 0; margin: 0; padding: 0; }
legend { font-weight: 600; font-size: 0.9rem; margin-bottom: 6px; padding: 0; }

/* ---- cards (settings, stores) */
.card { background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow); padding: 16px; margin-bottom: 16px; }
.card h2 { font-size: 1.1rem; margin-bottom: 10px; }
.list-rows { list-style: none; margin: 0; padding: 0; }
.list-rows > li { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-top: 1px solid var(--line); min-height: 52px; }
.list-rows > li:first-child { border-top: 0; }
.aisle { list-style: none; margin: 0; padding: 0; counter-reset: aisle; }
.aisle li { display: flex; align-items: center; gap: 6px; padding: 4px 4px 4px 8px; border: 1px solid var(--line); border-radius: 12px; margin-bottom: 6px; background: var(--surface); counter-increment: aisle; touch-action: none; }
.aisle li::before { content: counter(aisle); width: 1.6em; color: var(--muted); font-variant-numeric: tabular-nums; text-align: right; }
.aisle li.dragging { opacity: 0.5; }
.aisle li.over { border-color: var(--accent); }
.aisle .grip { cursor: grab; color: var(--muted); display: grid; place-items: center; width: 32px; height: var(--tap); }
.aisle .label { flex: 1; }
.tag { display: inline-block; font-size: 0.75rem; padding: 1px 8px; border-radius: 999px; background: var(--surface-2); color: var(--muted); }

/* ---- auth */
.auth { min-height: 100dvh; display: grid; place-items: center; padding: 16px; }
.auth-card { width: min(420px, 100%); background: var(--surface); border-radius: 22px; box-shadow: var(--shadow); padding: 28px 24px; }
.auth-brand { display: flex; align-items: center; gap: 12px; font-weight: 800; font-size: 1.4rem; margin-bottom: 18px; }
.auth-card h1 { font-size: 1.3rem; margin-bottom: 4px; }
.auth-card p.muted { margin: 0 0 16px; }
.invite-hint { margin-top: 16px; text-align: center; }

/* ---- toasts */
.toasts { position: fixed; left: 50%; transform: translateX(-50%); bottom: calc(16px + env(safe-area-inset-bottom)); z-index: 100; display: flex; flex-direction: column; gap: 8px; width: min(480px, calc(100vw - 24px)); pointer-events: none; }
.toast { background: #1c2433; color: #fff; border-radius: 14px; padding: 10px 10px 10px 16px; display: flex; gap: 10px; align-items: center; justify-content: space-between; box-shadow: 0 8px 24px rgb(0 0 0 / 25%); pointer-events: auto; }
.toast.error { background: #8c1d18; }
.toast button { background: transparent; color: #f3d27a; border: 0; font-weight: 700; min-height: 36px; padding: 0 8px; cursor: pointer; }
.spinner-wrap { display: inline-flex; gap: 10px; align-items: center; color: var(--muted); }
.spinner { width: 22px; height: 22px; border-radius: 50%; border: 3px solid var(--line); border-top-color: var(--accent); animation: spin 0.8s linear infinite; }
@media (max-width: 480px) { .hide-sm { display: none; } }
